import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2, IndianRupee } from 'lucide-react';
import { API_ENDPOINTS, apiCall } from '../../config/api';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// Records a payment against an invoice via PATCH /invoices/:id/status.
// The backend treats `partial_payment` as an increment on top of what is
// already recorded, so the form asks "how much was received now", never the
// running total. `paid` is only accepted once the running total covers the
// grand total; `unpaid` resets the status (amount untouched).
const UpdatePaymentModal = ({ invoice, shipmentNo, onClose, onUpdated }) => {
  const raw = invoice && (Array.isArray(invoice) ? invoice[0] : invoice);
  const inv = raw?.invoice || raw || {};
  const invoiceId = raw?.id ?? inv.id;
  const summary = inv.summary || inv;
  const grandTotal = Number(summary.grand_total ?? summary.grandTotal ?? 0) || 0;
  const currentStatus = String(raw?.status ?? inv.status ?? 'unpaid').toLowerCase();
  const paidSoFar = Number(raw?.partialPayment ?? raw?.partial_payment ?? inv.partialPayment ?? inv.partial_payment ?? 0) || 0;
  const balance = Math.max(grandTotal - paidSoFar, 0);

  const [mode, setMode] = useState(balance > 0 ? 'partial' : 'paid'); // partial | paid | unpaid
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const amountNum = Number(amount) || 0;
  const willBePaid = mode === 'paid' || (mode === 'partial' && paidSoFar + amountNum >= grandTotal && grandTotal > 0);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!invoiceId) { setError('Invoice id missing — cannot update.'); return; }

    let body;
    if (mode === 'unpaid') {
      body = { status: 'unpaid' };
    } else if (mode === 'paid') {
      // Full settlement: send whatever is still outstanding so the backend's
      // "paid only when total covered" rule is satisfied.
      body = balance > 0 ? { status: 'paid', partial_payment: balance } : { status: 'paid' };
    } else {
      if (!(amountNum > 0)) { setError('Enter the amount received'); return; }
      if (amountNum > balance + 0.005) { setError(`Amount cannot exceed the balance due (₹${fmt(balance)})`); return; }
      body = { status: willBePaid ? 'paid' : 'partially_paid', partial_payment: amountNum };
    }

    setSaving(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.INVOICE_UPDATE}/${invoiceId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        setError(data?.message || `Update failed (HTTP ${res.status})`);
        return;
      }
      toast.success(
        body.status === 'paid' ? 'Invoice marked as Paid' :
        body.status === 'unpaid' ? 'Invoice marked as Unpaid' :
        `₹${fmt(amountNum)} recorded — ₹${fmt(balance - amountNum)} still due`
      );
      onUpdated?.(data?.data || data);
      onClose();
    } catch (err) {
      console.error('Update payment error:', err);
      setError('Unable to reach the server. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Resulting status for the currently selected action — drives the chips.
  const targetStatus = mode === 'unpaid' ? 'unpaid' : willBePaid ? 'paid' : 'partially_paid';
  const STATUS_CHIPS = [
    { value: 'unpaid', label: 'Unpaid', mode: 'unpaid', on: 'bg-red-500 text-white border-red-500', off: 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20', current: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-400' },
    { value: 'partially_paid', label: 'Partially Paid', mode: 'partial', on: 'bg-amber-500 text-white border-amber-500', off: 'text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20', current: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-400' },
    { value: 'paid', label: 'Paid', mode: 'paid', on: 'bg-emerald-600 text-white border-emerald-600', off: 'text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20', current: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-400' },
  ];
  const chipDisabled = (c) =>
    (c.mode === 'partial' && balance <= 0) ||
    (c.mode === 'paid' && currentStatus === 'paid' && balance <= 0) ||
    (c.mode === 'unpaid' && currentStatus === 'unpaid');

  const Option = ({ value, label, hint, disabled }) => (
    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    } ${mode === value ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}>
      <input type="radio" name="mode" value={value} checked={mode === value} disabled={disabled} onChange={() => { setMode(value); setError(''); }} className="mt-1" />
      <span>
        <span className="block text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">{hint}</span>
      </span>
    </label>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} noValidate className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Update Payment</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">AWB / BL {shipmentNo}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-2.5">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Invoice</div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">₹{fmt(grandTotal)}</div>
            </div>
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2.5">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Paid</div>
              <div className="text-sm font-bold text-emerald-700 dark:text-emerald-300">₹{fmt(paidSoFar)}</div>
            </div>
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-2.5">
              <div className="text-[11px] uppercase tracking-wide text-red-700 dark:text-red-300">Due</div>
              <div className="text-sm font-bold text-red-700 dark:text-red-300">₹{fmt(balance)}</div>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Status</div>
            <div className="flex gap-2">
              {STATUS_CHIPS.map((c) => {
                const active = targetStatus === c.value;
                const isCurrent = currentStatus === c.value;
                const disabled = chipDisabled(c);
                // A disabled chip that is the current status still needs to read
                // clearly — it's telling the user "you're already here".
                const cls = active ? c.on : disabled && isCurrent ? c.current : c.off;
                return (
                  <button
                    key={c.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => { setMode(c.mode); setError(''); }}
                    className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-semibold transition ${disabled ? 'cursor-not-allowed' : ''} ${disabled && !isCurrent ? 'opacity-50' : ''} ${cls}`}
                    title={isCurrent ? 'Current status' : undefined}
                  >
                    {c.label}
                    {isCurrent && <span className={`block text-[10px] font-normal ${active ? 'text-white/80' : 'opacity-70'}`}>current</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Option value="partial" label="Record a payment" hint="Add the amount received now to what is already paid" disabled={balance <= 0} />
            <Option value="paid" label="Mark as fully Paid" hint={balance > 0 ? `Records the remaining ₹${fmt(balance)} and closes the invoice` : 'Invoice is already fully paid'} disabled={currentStatus === 'paid' && balance <= 0} />
            <Option value="unpaid" label="Mark as Unpaid" hint="Reset the status (recorded amount is kept)" disabled={currentStatus === 'unpaid'} />
          </div>

          {mode === 'partial' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount received now <span className="text-red-500">*</span></label>
              <div className="relative">
                <IndianRupee size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(''); }}
                  placeholder={`Up to ${fmt(balance)}`}
                  autoFocus
                  className={`w-full pl-9 pr-3 py-2.5 border ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`}
                />
              </div>
              {amountNum > 0 && !error && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {willBePaid ? 'This settles the invoice — status will become Paid.' : `After this: ₹${fmt(paidSoFar + amountNum)} paid, ₹${fmt(balance - amountNum)} due.`}
                </p>
              )}
            </div>
          )}

          {error && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{error}</div>}
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 font-medium text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {saving && <Loader2 size={16} className="animate-spin" />}
            {mode === 'paid' ? 'Mark Paid' : mode === 'unpaid' ? 'Mark Unpaid' : 'Save Payment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdatePaymentModal;
